import {useIntl} from 'react-intl';
import type {ReactNode} from 'react';
import {ChevronLeft, Ellipsis, Lock, MicOff, Phone, PhoneOff, RefreshCw, Video} from 'lucide-react';
import {useAppDispatch} from '../../../store/store';
import {CallMenu, type CallMenuItem} from '../components/CallMenu';
import {CallKeypad} from '../components/CallKeypad';
import {OutputPicker} from '../components/OutputPicker';
import {BubbleActionButton, BubbleChip, BubbleControl, BubbleEndButton, BubbleIconButton, ExpandButton, StatusNote} from '../components/controls';
import {useElapsed} from '../hooks/useElapsed';
import type {MatrixCallLeg} from '../model/types';
import type {ControlSpec} from '../group/useGroupControls';
import {groupSurfaceMessages as gs} from '../group/groupMessages';
import {FeedVideo, PeerAvatar} from './MatrixMedia';
import {matrixActions} from './matrixActions';
import {hasLiveVideo, type MatrixCallMedia} from './useMatrixCallMedia';
import {useReliefView} from '../videoRelief/videoReliefStore';
import {PausedVideoButton} from '../videoRelief/VideoReliefToast';
import type {MatrixControls} from './useMatrixControls';
import {matrixSurfaceMessages as m} from './matrixMessages';

const nameOf = (leg: MatrixCallLeg, fallback: string): string => leg.identity.name ?? fallback;

const SpecButton = ({spec}: {spec: ControlSpec}) => (
    <BubbleControl
        icon={spec.icon}
        label={spec.label}
        tone={spec.tone}
        alert={spec.alert}
        pressed={spec.pressed}
        hasPopup={spec.hasPopup}
        disabled={spec.disabled}
        testId={spec.testId}
        onClick={spec.onClick}
    />
);

const MoreMenu = ({items}: {items: readonly ControlSpec[]}) => {
    const intl = useIntl();
    const menuItems: CallMenuItem[] = items.map(item => ({id: item.id, label: item.label, icon: item.icon, testId: item.testId, onSelect: item.onClick}));
    return (
        <CallMenu
            items={menuItems}
            placement="above-end"
            trigger={({open, toggle}) => (
                <BubbleControl
                    icon={<Ellipsis size={20} aria-hidden="true"/>}
                    label={intl.formatMessage(gs.moreOptions)}
                    tone={open ? 'pressed' : 'default'}
                    expanded={open}
                    hasPopup="menu"
                    testId="one-to-one-more"
                    onClick={toggle}
                />
            )}
        />
    );
};

const DirectChip = ({leg, video = leg.kind === 'video', timer}: {leg: MatrixCallLeg; video?: boolean; timer?: string}) => {
    const intl = useIntl();
    const label = timer
        ? intl.formatMessage(video ? m.chipVideoTimer : m.chipVoiceTimer, {timer})
        : intl.formatMessage(video ? m.chipVideo : m.chipVoice);
    return <BubbleChip icon={<Lock size={13} aria-hidden="true"/>}>{label}</BubbleChip>;
};

export const MatrixIncomingBubble = ({leg}: {leg: MatrixCallLeg}) => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const name = nameOf(leg, intl.formatMessage(gs.you));
    const video = leg.kind === 'video';
    return (
        <div className="phq-call-swap flex flex-col gap-3" data-testid={video ? 'one-to-one-video-ring' : 'one-to-one-ring'}>
            <div className="flex min-h-8 items-center">
                <DirectChip leg={leg}/>
            </div>
            <div className="flex flex-col items-center gap-2 text-center">
                <span className="phq-call-ring rounded-[20px]"><PeerAvatar leg={leg} size={64}/></span>
                <p className="text-lg font-semibold leading-[1.2]">{name}</p>
                <p className="text-sm font-medium text-[var(--phq-call-ink-muted)]">{intl.formatMessage(video ? m.incomingVideo : m.incomingCall)}</p>
            </div>
            <div className="flex gap-2">
                <BubbleActionButton
                    tone="danger"
                    icon={<PhoneOff size={18} aria-hidden="true"/>}
                    label={intl.formatMessage(m.decline)}
                    testId="one-to-one-decline"
                    onClick={() => matrixActions.decline(leg.matrixCallId, dispatch)}
                />
                <BubbleActionButton
                    tone="accept"
                    icon={video ? <Video size={18} aria-hidden="true"/> : <Phone size={18} aria-hidden="true"/>}
                    label={intl.formatMessage(m.accept)}
                    ariaLabel={intl.formatMessage(video ? m.acceptVideoAria : m.acceptAria, {name})}
                    testId={video ? 'one-to-one-answer-video' : 'one-to-one-answer'}
                    onClick={() => matrixActions.answer(leg.matrixCallId, video, dispatch)}
                />
            </div>
            {video && (
                <button
                    type="button"
                    data-testid="one-to-one-answer-audio"
                    onClick={() => matrixActions.answer(leg.matrixCallId, false, dispatch)}
                    className="phq-call-press inline-flex h-10 w-full items-center justify-center rounded-2xl bg-[var(--phq-call-surface-muted)] text-[15px] font-semibold"
                >
                    {intl.formatMessage(m.answerAudioOnly)}
                </button>
            )}
        </div>
    );
};

export const MatrixCallingBubble = ({leg, onExpand}: {leg: MatrixCallLeg; onExpand: () => void}) => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const connecting = leg.direction === 'incoming';
    return (
        <div className="phq-call-swap flex flex-col gap-3" data-testid="one-to-one-call-surface">
            <div className="flex min-h-8 items-center justify-between gap-2">
                <DirectChip leg={leg}/>
                <ExpandButton label={intl.formatMessage(gs.expandCall)} onClick={onExpand}/>
            </div>
            <div className="flex flex-col items-center gap-2 text-center">
                <span className="phq-call-ring rounded-[20px]"><PeerAvatar leg={leg} size={64}/></span>
                <p className="text-lg font-semibold leading-[1.2]">{nameOf(leg, '')}</p>
                <p className="text-sm font-medium text-[var(--phq-call-ink-muted)]">{intl.formatMessage(connecting ? m.connecting : m.calling)}</p>
            </div>
            <BubbleEndButton label={intl.formatMessage(connecting ? m.endCall : m.cancel)} testId="one-to-one-hangup" onClick={() => matrixActions.end(leg.matrixCallId, dispatch)}/>
        </div>
    );
};

const VideoArea = ({leg, media}: {leg: MatrixCallLeg; media: MatrixCallMedia}) => {
    const intl = useIntl();
    const remote = media.feeds.remoteScreen ?? media.feeds.remoteUsermedia;
    const relief = useReliefView();
    const remoteLive = !!media.feeds.remoteScreen || (hasLiveVideo(media.feeds.remoteUsermedia) && !relief.remotePaused);
    const local = media.feeds.localUsermedia;
    const localLive = hasLiveVideo(local);
    return (
        <div className="relative h-44 overflow-hidden rounded-[14px] bg-[var(--phq-call-surface)]">
            {remote && remoteLive ? (
                <FeedVideo feed={remote} contain={!!media.feeds.remoteScreen} testId="one-to-one-remote-video"/>
            ) : (
                <div className="flex h-full flex-col items-center justify-center gap-2">
                    <PeerAvatar leg={leg} size={64}/>
                    {relief.remotePaused
                        ? <PausedVideoButton compact/>
                        : <span className="text-xs font-semibold text-[var(--phq-call-ink-muted)]">{intl.formatMessage(m.remoteCameraOff)}</span>}
                </div>
            )}
            <span className="absolute bottom-1.5 left-1.5 inline-flex h-[22px] items-center gap-1.5 rounded-lg bg-[var(--phq-call-chip-scrim)] px-1.5 text-xs font-semibold">
                {nameOf(leg, '')}
            </span>
            <div className="absolute right-2 top-2 flex h-12 w-[72px] items-center justify-center overflow-hidden rounded-[10px] border border-[var(--phq-call-line-strong)] bg-[var(--phq-call-canvas)]">
                {local && localLive ? (
                    <FeedVideo feed={local} mirror testId="one-to-one-local-video"/>
                ) : (
                    <span className="text-xs font-semibold text-[var(--phq-call-ink-muted)]">{intl.formatMessage(gs.cameraOff)}</span>
                )}
            </div>
        </div>
    );
};

interface MatrixActiveBubbleProps {
    readonly leg: MatrixCallLeg;
    readonly media: MatrixCallMedia;
    readonly controls: MatrixControls;
    readonly onExpand: () => void;
}

export const MatrixActiveBubble = ({leg, media, controls, onExpand}: MatrixActiveBubbleProps) => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const timer = useElapsed(leg.connectedAt);
    const name = nameOf(leg, '');
    const video = leg.kind === 'video' || hasLiveVideo(media.feeds.localUsermedia) || hasLiveVideo(media.feeds.remoteUsermedia) || !!media.feeds.remoteScreen;
    const reconnecting = leg.network === 'reconnecting';
    const primary = (video
        ? [controls.microphone, controls.camera, controls.share, controls.addCall]
        : [controls.microphone, controls.keypad, controls.speaker, controls.hold, controls.addCall]
    ).filter((spec): spec is ControlSpec => spec !== undefined);
    const overflow = (video
        ? [controls.hold, controls.keypad, controls.speaker, controls.devices]
        : [controls.camera, controls.share, controls.devices]
    ).filter((spec): spec is ControlSpec => spec !== undefined);
    const subline = leg.heldByPeer
        ? intl.formatMessage(m.heldByPeer, {name})
        : leg.held && timer
            ? intl.formatMessage(m.onHold, {timer})
            : timer;
    return (
        <div className="phq-call-swap flex flex-col gap-3" data-testid="one-to-one-call-surface">
            <div className="flex min-h-8 items-center justify-between gap-2">
                <DirectChip leg={leg} video={video} timer={video ? timer : undefined}/>
                <ExpandButton label={intl.formatMessage(gs.expandCall)} onClick={onExpand}/>
            </div>
            {video ? (
                <VideoArea leg={leg} media={media}/>
            ) : (
                <div className="flex flex-col items-center gap-2 text-center">
                    <PeerAvatar leg={leg} size={64}/>
                    <p className="text-lg font-semibold leading-[1.2]">{name}</p>
                    <p className="text-sm font-medium tabular-nums text-[var(--phq-call-ink-muted)]">{subline}</p>
                </div>
            )}
            {reconnecting && <StatusNote tone="reconnecting" icon={<RefreshCw size={16} className="animate-spin" aria-hidden="true"/>}>{intl.formatMessage(gs.noteReconnecting)}</StatusNote>}
            {!reconnecting && leg.muted && <StatusNote tone="muted" icon={<MicOff size={16} aria-hidden="true"/>}>{intl.formatMessage(gs.noteMuted)}</StatusNote>}
            <div className={`flex items-center justify-between ${reconnecting ? 'opacity-40' : ''}`}>
                {primary.map(spec => <SpecButton key={spec.id} spec={spec}/>)}
                {overflow.length > 0 && <MoreMenu items={overflow}/>}
            </div>
            <BubbleEndButton label={intl.formatMessage(m.endCall)} testId="one-to-one-hangup" onClick={() => matrixActions.end(leg.matrixCallId, dispatch)}/>
        </div>
    );
};

export const MatrixPanelBubble = ({title, onBack, children}: {title: string; onBack: () => void; children: ReactNode}) => {
    const intl = useIntl();
    return (
        <div className="phq-call-swap flex flex-col gap-3">
            <div className="flex items-center gap-3">
                <BubbleIconButton label={intl.formatMessage(m.backToCall)} icon={<ChevronLeft size={16} aria-hidden="true"/>} onClick={onBack}/>
                <h2 className="text-[15px] font-semibold">{title}</h2>
            </div>
            {children}
        </div>
    );
};

export const MatrixKeypadBubble = ({onBack}: {onBack: () => void}) => {
    const intl = useIntl();
    return (
        <MatrixPanelBubble title={intl.formatMessage(m.keypadTitle)} onBack={onBack}>
            <CallKeypad onDigit={digit => void matrixActions.dtmf(digit)}/>
        </MatrixPanelBubble>
    );
};

export const MatrixOutputBubble = ({onBack}: {onBack: () => void}) => {
    const intl = useIntl();
    return (
        <MatrixPanelBubble title={intl.formatMessage(m.outputTitle)} onBack={onBack}>
            <OutputPicker onSelect={deviceId => matrixActions.switchDevice('audiooutput', deviceId)}/>
        </MatrixPanelBubble>
    );
};
